import { XlsxAnchor, xlsxUrl } from '../types/xlsxAnchor';
export interface SourceNavigationTarget {
  xlsxAnchor?: XlsxAnchor;
  id?: string;
  fileId?: string;
  documentId?: string;
  sourceId?: string;
  title?: string;
  fileName?: string;
  excerpt?: string;
  snippet?: string;
  pageNumber?: number | string | null;
  page_number?: number | string | null;
}

const SEARCH_PHRASE_PATTERNS: RegExp[] = [
  /(.+?)\s*(?:に関して|について|に対して)\s*(?:検索|調べ|教え|説明し|まとめ)て(?:ください)?[。.!?！？]*$/,
  /(.+?)\s*(?:を|で|の)\s*(?:検索|調べ|教え)て(?:ください)?[。.!?！？]*$/,
  /(.+?)\s*(?:って何|とは何|とは|って)(?:\?|？)?$/,
  /(.+?)\s*を\s*(?:検索|調べて|教えて)(?:ください)?[。.!?！？]*$/,
  /^(?:please\s+)?tell\s+me\s+about\s+(.+?)[。.!?！？]*$/i,
  /^(?:please\s+)?(?:search\s+(?:for\s+)?|find\s+)(.+?)[。.!?！？]*$/i,
];

const IDENTIFIER_PATTERN = /[A-Za-z][A-Za-z0-9_-]*\d+[A-Za-z0-9_-]*|[A-Z]{2,}[A-Za-z0-9_-]*/g;
const JAPANESE_ANCHOR_PATTERN = /[一-龯々〆ヵヶぁ-んァ-ヴー・]{4,}/g;
const PAGE_MARKER_PATTERNS: Array<{ pattern: RegExp; totalGroup?: number }> = [
  { pattern: /-{2,}\s*Page\s+(\d+)\s*-{2,}/i },
  { pattern: /-{2,}\s*ページ\s*(\d+)\s*-{2,}/ },
];
const PRINTED_PAGE_MARKER_PATTERN = /(?:^|[^\d])(\d{1,4})\s*\/\s*(\d{1,4})(?:[^\d]|$)/;

const normalizeToken = (value: string) => value.toLowerCase();

export const parsePositivePage = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const page = typeof value === 'number' ? value : parseInt(value, 10);
  return Number.isInteger(page) && page > 0 ? page : null;
};

const sourceText = (source?: SourceNavigationTarget): string => {
  if (!source) return '';
  return [source.excerpt, source.snippet, source.title, source.fileName]
    .filter(Boolean)
    .join(' ');
};

const sourceContentText = (source?: SourceNavigationTarget): string => {
  if (!source) return '';
  return [source.excerpt, source.snippet].filter(Boolean).join(' ');
};

const findPageMarker = (source?: SourceNavigationTarget): number | null => {
  const text = sourceContentText(source);
  for (const { pattern, totalGroup } of PAGE_MARKER_PATTERNS) {
    const match = text.match(pattern);
    const page = parsePositivePage(match?.[1]);
    const total = totalGroup ? parsePositivePage(match?.[totalGroup]) : null;
    if (totalGroup && (!total || !page || page > total || total > 1000)) {
      continue;
    }
    if (page) return page;
  }
  return null;
};

const findPrintedPageMarker = (source?: SourceNavigationTarget): number | null => {
  const match = sourceContentText(source).match(PRINTED_PAGE_MARKER_PATTERN);
  const page = parsePositivePage(match?.[1]);
  const total = parsePositivePage(match?.[2]);
  if (!page || !total || page > total || total > 1000) return null;
  return page;
};

const findSharedIdentifier = (query: string, source?: SourceNavigationTarget): string => {
  const sourceIdentifiers = new Set(
    (sourceText(source).match(IDENTIFIER_PATTERN) || []).map(normalizeToken),
  );
  if (sourceIdentifiers.size === 0) return '';

  for (const token of query.match(IDENTIFIER_PATTERN) || []) {
    if (sourceIdentifiers.has(normalizeToken(token))) {
      return token;
    }
  }
  return '';
};

const escapeRegExp = (value: string): string => (
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
);

const findSourceAnchorNearIdentifier = (
  query: string,
  source?: SourceNavigationTarget,
): string => {
  const identifier = findSharedIdentifier(query, source);
  if (!identifier) return '';

  const text = sourceContentText(source).replace(/\s+/g, ' ').trim();
  if (!text) return '';

  const compactText = text.replace(/\s+/g, '');
  const compactIdentifier = identifier.replace(/\s+/g, '');
  const preferredExactAnchors = [
    '添付資料として以下',
  ];
  for (const anchor of preferredExactAnchors) {
    if (compactText.includes(anchor)) {
      return anchor;
    }
  }

  const preferredSuffixes = [
    '資料には以下',
    '資料に対する質疑応答',
    'を開催するに当たり',
  ];
  for (const suffix of preferredSuffixes) {
    if (compactText.includes(`${compactIdentifier}${suffix}`)) {
      return suffix;
    }
  }

  const match = text.match(new RegExp(escapeRegExp(identifier), 'i'));
  if (!match || match.index === undefined) return '';

  const before = text.slice(Math.max(0, match.index - 48), match.index);
  const candidates = before.match(JAPANESE_ANCHOR_PATTERN) || [];
  return candidates[candidates.length - 1] || '';
};

export const extractSearchKeyword = (
  query: string,
  source?: SourceNavigationTarget,
): string => {
  const trimmed = query.trim();
  if (!trimmed) return '';

  const sharedIdentifier = findSharedIdentifier(trimmed, source);
  if (sharedIdentifier) return sharedIdentifier;

  for (const pattern of SEARCH_PHRASE_PATTERNS) {
    const match = trimmed.match(pattern);
    if (match?.[1]) {
      return match[1].trim();
    }
  }

  return trimmed;
};

const extractSourceAnchoredKeyword = (
  query: string,
  source?: SourceNavigationTarget,
): string => (
  findSourceAnchorNearIdentifier(query, source) || extractSearchKeyword(query, source)
);

export const resolveInitialPage = (
  pageParam: string | null,
  fallbackPage = 1,
): number => {
  if (!pageParam) {
    return parsePositivePage(fallbackPage) || 1;
  }
  return parsePositivePage(pageParam) || 1;
};

export const getSourceDocumentId = (source: SourceNavigationTarget): string => (
  source.fileId || source.documentId || source.sourceId || source.id || ''
);

// A highlight term can only match the PDF text layer if it literally
// occurs in the document. Natural-language questions almost never do —
// in ANY language ("How are digital technologies used?" over a Japanese
// PDF, or 「〜はどこですか？」 which is the question, not the passage).
// When the derived keyword does not occur in the matched excerpt, anchor
// the highlight to the excerpt itself: it was extracted from this very
// document, so it can actually match (#783).

// Anchor-run character class. Includes half-width katakana
// (U+FF66-FF9F, legacy business docs) and CJK compatibility ideographs
// (U+FA00-FAFF, name variants like 﨑) so those excerpts still yield an
// anchor. Excludes ・ deliberately: OCR layers flip between ・(U+30FB)
// and ･(U+FF65), so a compound containing it would silently never match
// — splitting at it leaves two runs that each match fine.
const ANCHOR_RUN_PATTERN = /[一-鿿々〆ヵヶぁ-んァ-ヴーｦ-ﾟ切-﫿]{4,}/g;
const ANCHOR_MAX_CHARS = 12;
// Page markers like "--- Page 3 ---" live in excerpt METADATA, not in
// the PDF text layer — they must never become highlight anchors.
const PAGE_MARKER_STRIP = /-{2,}\s*(?:Page|ページ)\s*\d+\s*-{2,}/gi;

const extractExcerptAnchor = (source?: SourceNavigationTarget): string => {
  // Anchor within a SINGLE field: excerpt and snippet can hold different
  // text, and a run spanning their join would exist nowhere in the PDF.
  const field = (source?.excerpt || source?.snippet || '').replace(PAGE_MARKER_STRIP, ' ');
  const compact = field.replace(/\s+/g, '');
  if (!compact) return '';

  // Prefer the FIRST substantial run (sentence start — natural to read
  // in the viewer's search box); fall back to the longest. Compact
  // input is robust against character-by-character Japanese extraction.
  const runs = compact.match(ANCHOR_RUN_PATTERN) || [];
  const first = runs.find((run) => run.length >= 6);
  const longest = [...runs].sort((a, b) => b.length - a.length)[0];
  const run = first || longest;
  if (run) return run.slice(0, ANCHOR_MAX_CHARS);

  // Latin excerpts: leading words as ONE bounded phrase.
  const words = field.trim().split(/\s+/).filter(Boolean);
  return words.slice(0, 4).join(' ').slice(0, 40);
};

const keywordOccursInExcerpt = (
  keyword: string,
  source?: SourceNavigationTarget,
): boolean => {
  const compactExcerpt = sourceContentText(source).replace(/\s+/g, '').toLowerCase();
  const compactKeyword = keyword.replace(/\s+/g, '').toLowerCase();
  if (!compactExcerpt || !compactKeyword) return false;
  return compactExcerpt.includes(compactKeyword);
};

// Identifier-like single tokens (product codes, standard numbers) are
// worth searching document-wide even when the excerpt doesn't repeat
// them — they often come from the file NAME and do occur in the body.
const isIdentifierLike = (keyword: string): boolean => (
  /^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(keyword)
);

export const buildSourceDocumentUrl = (
  source: SourceNavigationTarget,
  query = '',
): string => {
  if (source.xlsxAnchor) return xlsxUrl(source.xlsxAnchor);
  const documentId = getSourceDocumentId(source);
  if (!documentId) return '';

  const params = new URLSearchParams();
  const page = parsePositivePage(source.pageNumber ?? source.page_number) ?? findPageMarker(source);
  if (page) {
    params.set('page', String(page));
  } else {
    const approximatePage = findPrintedPageMarker(source);
    if (approximatePage) {
      params.set('approxPage', String(approximatePage));
    }
  }

  let keyword = page
    ? extractSearchKeyword(query, source)
    : extractSourceAnchoredKeyword(query, source);
  // Shared identifiers (matched between the query and the source's
  // title/filename/excerpt) are the user's own search subject — never
  // replace them, even when the excerpt itself doesn't repeat them
  // (e.g. code in the FILENAME, prose excerpt).
  const sharedIdentifier = findSharedIdentifier(query, source);
  if (
    keyword
    && keyword !== sharedIdentifier
    && !keywordOccursInExcerpt(keyword, source)
  ) {
    const anchor = extractExcerptAnchor(source);
    if (anchor) {
      keyword = anchor;
    } else if (!isIdentifierLike(keyword.trim())) {
      // No excerpt to anchor to, and a natural-language question (in
      // any language — Japanese ones contain no whitespace) cannot
      // match the PDF text layer. Page-only navigation beats a junk
      // term sitting in the viewer's search box.
      keyword = '';
    }
  }
  if (keyword) {
    params.set('highlight', keyword);
  }

  const queryString = params.toString();
  return queryString
    ? `/documents/${documentId}?${queryString}`
    : `/documents/${documentId}`;
};
